import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, KeyRound, Shield, User as UserIcon, X, CreditCard, Activity, Mail, AlertTriangle, BarChart3, Pencil, Eye, Copy, LogIn, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const LICENSE_TYPE_LABELS = {
  single_use: "Estudio (sin consultoría)",
  subscription_monthly: "Suscripción mensual",
  subscription_annual: "Suscripción anual",
};

const LICENSE_TYPE_BADGES = {
  single_use: "bg-amber-50 text-amber-800 border-amber-200",
  subscription_monthly: "bg-blue-50 text-blue-800 border-blue-200",
  subscription_annual: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffH = diffMs / 3_600_000;
  if (diffH < 1) return "hace minutos";
  if (diffH < 24) return `hace ${Math.floor(diffH)} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `hace ${diffD} d`;
  return d.toLocaleDateString("es-ES");
}

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

const INACTIVITY_THRESHOLD_DAYS = 30;

function isInactiveUser(u) {
  if (u.role === "admin") return false;
  // Sólo cuenta como inactivo un usuario que YA tiene plan (no leads sin plan asignado).
  if (!u.license_type) return false;
  const ds = daysSince(u.last_login_at);
  if (ds == null) return true;            // nunca ha entrado y ya tiene plan → 100% inactivo
  return ds >= INACTIVITY_THRESHOLD_DAYS;  // > 30 días
}

function userHasRealEmail(u) {
  const e = String(u.email || "").toLowerCase();
  return e && !e.endsWith("@farmaclass.local");
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });
  const [resetFor, setResetFor] = useState(null);
  const [credsFor, setCredsFor] = useState(null);
  const [credsData, setCredsData] = useState(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [newPass, setNewPass] = useState("");
  const [planFor, setPlanFor] = useState(null);
  const [planForm, setPlanForm] = useState({ license_type: "subscription_monthly", billing_consent: true, reset_studies: false });
  const [filter, setFilter] = useState("all"); // "all" | "inactive"
  const [reminderLoading, setReminderLoading] = useState({});
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", name: "", pharmacy_name: "", phone: "", province: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [provincesCCAA, setProvincesCCAA] = useState({});

  const load = () => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Sólo administradores pueden ver esto");
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast.error("Email y contraseña son obligatorios");
      return;
    }
    try {
      await api.post("/admin/users", form);
      toast.success("Usuario creado");
      setForm({ email: "", password: "", name: "", role: "user" });
      setShowForm(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al crear");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar usuario ${u.email} y TODOS sus datos? Esta acción no se puede deshacer.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    toast.success("Usuario eliminado");
    load();
  };

  const resetPassword = async () => {
    if (!newPass || newPass.length < 4) {
      toast.error("Mínimo 4 caracteres");
      return;
    }
    await api.post(`/admin/users/${resetFor.id}/password`, { password: newPass });
    toast.success("Contraseña actualizada");
    setResetFor(null);
    setNewPass("");
  };

  const openCredentials = async (u) => {
    setCredsFor(u);
    setCredsData(null);
    setCredsLoading(true);
    try {
      const r = await api.get(`/admin/users/${u.id}/credentials`);
      setCredsData(r.data);
    } catch (e) {
      toast.error("No se pudo cargar la contraseña");
      setCredsFor(null);
    } finally {
      setCredsLoading(false);
    }
  };

  const generatePassword = async () => {
    if (!credsFor) return;
    try {
      const r = await api.post(`/admin/users/${credsFor.id}/reset-password`, {});
      const emailStatus = r.data?.email_status;
      if (emailStatus === "sent") {
        toast.success(`Contraseña regenerada · email enviado a ${credsFor.email}. Cópiala ahora, no se volverá a mostrar.`);
      } else if (emailStatus === "failed") {
        toast.warning(`Contraseña regenerada · el email NO se pudo enviar. Copia la contraseña y envíala manualmente.`);
      } else {
        toast.success("Contraseña regenerada · cópiala ahora, no se volverá a mostrar");
      }
      setCredsData({
        ...credsData,
        current_password: r.data.new_password,
        password_changed_at: new Date().toISOString(),
      });
    } catch {
      toast.error("No se pudo regenerar");
    }
  };

  const copyText = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const openPlanChange = (u) => {
    setPlanFor(u);
    setPlanForm({
      license_type: u.license_type === "single_use" ? "subscription_monthly" : (u.license_type || "subscription_monthly"),
      billing_consent: true,
      reset_studies: u.license_type === "single_use",
    });
  };

  const savePlan = async () => {
    const isSub = planForm.license_type !== "single_use";
    if (isSub && !planForm.billing_consent) {
      toast.error("Marca el consentimiento de facturación para las suscripciones");
      return;
    }
    try {
      await api.post(`/admin/users/${planFor.id}/plan`, planForm);
      toast.success("Plan actualizado");
      setPlanFor(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al cambiar el plan");
    }
  };

  const openEdit = (u) => {
    setEditFor(u);
    setEditForm({
      email: u.email || "",
      name: u.name || "",
      pharmacy_name: u.pharmacy_name || "",
      phone: u.phone || "",
      province: u.province || "",
    });
    // Carga la lista de provincias sólo si aún no la tenemos (una vez por sesión)
    if (provinces.length === 0) {
      api
        .get("/admin/provinces")
        .then((r) => {
          setProvinces(r.data?.provinces || []);
          setProvincesCCAA(r.data?.provinces_ccaa || {});
        })
        .catch(() => { /* silencioso, el select queda vacío pero el modal sigue usable */ });
    }
  };

  const saveEdit = async () => {
    if (!editFor) return;
    const trimmedEmail = (editForm.email || "").trim().toLowerCase();
    if (!trimmedEmail) {
      toast.error("El email no puede estar vacío");
      return;
    }
    setSavingEdit(true);
    try {
      const r = await api.patch(`/admin/users/${editFor.id}`, {
        email: trimmedEmail,
        name: editForm.name,
        pharmacy_name: editForm.pharmacy_name,
        phone: editForm.phone,
        province: editForm.province,
      });
      if (r.data?.updated) {
        toast.success("Datos actualizados");
      } else {
        toast.info("Sin cambios");
      }
      setEditFor(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al actualizar");
    } finally {
      setSavingEdit(false);
    }
  };

  const sendReminder = async (u) => {
    if (!userHasRealEmail(u)) {
      toast.error("Este usuario no tiene email real (sólo acceso por licencia)");
      return;
    }
    const ds = daysSince(u.last_login_at);
    const tag = u.last_login_at ? `(${ds} días sin entrar)` : "(nunca ha entrado)";
    if (!window.confirm(`Enviar recordatorio a ${u.email} ${tag}?`)) return;
    setReminderLoading((s) => ({ ...s, [u.id]: true }));
    try {
      await api.post(`/admin/users/${u.id}/send-reminder`);
      toast.success(`Recordatorio enviado a ${u.email}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar el recordatorio");
    } finally {
      setReminderLoading((s) => ({ ...s, [u.id]: false }));
    }
  };

  const sendWelcomeAccess = async (u) => {
    if (!userHasRealEmail(u)) {
      toast.error("Este usuario no tiene email real");
      return;
    }
    if (!window.confirm(`Enviar acceso (email + URL, SIN resetear contraseña) a ${u.email}?`)) return;
    setReminderLoading((s) => ({ ...s, [`wc-${u.id}`]: true }));
    try {
      await api.post(`/admin/users/${u.id}/send-welcome-email`);
      toast.success(`Email de acceso enviado a ${u.email}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar el email");
    } finally {
      setReminderLoading((s) => ({ ...s, [`wc-${u.id}`]: false }));
    }
  };

  const toggleBlock = async (u) => {
    if (u.is_blocked) {
      if (!window.confirm(`¿Reactivar el acceso de ${u.email}?`)) return;
      try {
        await api.post(`/admin/users/${u.id}/unblock`);
        toast.success("Acceso reactivado");
        load();
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "No se pudo desbloquear");
      }
      return;
    }
    const reason = window.prompt(
      `Vas a BLOQUEAR el acceso de ${u.email}.\n` +
      `Cuando intente entrar verá el mensaje "Su suscripción ha caducado" con tu contacto.\n\n` +
      `Motivo interno (opcional, sólo lo verás tú):`,
      "",
    );
    if (reason === null) return; // cancelado
    try {
      await api.post(`/admin/users/${u.id}/block`, { reason: reason || "" });
      toast.success(`Acceso bloqueado: ${u.email}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo bloquear");
    }
  };

  const sendMonthlySummary = async (u) => {
    if (!userHasRealEmail(u)) {
      toast.error("Este usuario no tiene email real (sólo acceso por licencia)");
      return;
    }
    if (!u.studies_used || u.studies_used < 1) {
      toast.error("Este cliente todavía no ha subido ningún estudio");
      return;
    }
    if (!window.confirm(`Enviar resumen mensual de FarmaClass a ${u.email}?`)) return;
    setReminderLoading((s) => ({ ...s, [`sum-${u.id}`]: true }));
    try {
      const r = await api.post(`/admin/users/${u.id}/send-monthly-summary`);
      if (r.data?.sent) {
        toast.success(`Resumen enviado a ${u.email}`);
      } else {
        toast.error(`No se pudo enviar: ${r.data?.reason || "error desconocido"}`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar el resumen");
    } finally {
      setReminderLoading((s) => ({ ...s, [`sum-${u.id}`]: false }));
    }
  };

  const inactiveCount = useMemo(() => users.filter(isInactiveUser).length, [users]);
  const visibleUsers = useMemo(
    () => (filter === "inactive" ? users.filter(isInactiveUser) : users),
    [users, filter],
  );

  const bulkSendReminders = async () => {
    const targets = users.filter((u) => isInactiveUser(u) && userHasRealEmail(u));
    if (!targets.length) {
      toast.info("No hay inactivos con email real para avisar");
      return;
    }
    if (!window.confirm(`Enviar recordatorio a ${targets.length} farmacia(s) inactiva(s)?`)) return;
    let ok = 0, fail = 0;
    for (const u of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/admin/users/${u.id}/send-reminder`);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    toast.success(`Enviados ${ok} recordatorio(s)` + (fail ? ` · ${fail} fallaron` : ""));
    load();
  };

  if (loading) return <div className="p-12 text-slate-400">Cargando…</div>;

  if (me?.role !== "admin") {
    return (
      <div className="p-12">
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
          Esta sección sólo está disponible para administradores.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-[1600px]" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Administración</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Usuarios y farmacias</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            Crea credenciales, cambia de plan a tus clientes y monitoriza su actividad (logins).
          </p>
        </div>
        <button
          data-testid="add-user-button"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Crear usuario
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          data-testid="user-form"
          className="bg-white border border-slate-200 rounded-lg p-6 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3"
        >
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            required
            data-testid="user-email-input"
          />
          <input
            type="text"
            placeholder="Nombre"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="px-3 py-2.5 border border-slate-200 rounded-md"
            data-testid="user-name-input"
          />
          <input
            type="text"
            placeholder="Contraseña"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="px-3 py-2.5 border border-slate-200 rounded-md font-mono-num"
            required
            data-testid="user-password-input"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="px-3 py-2.5 border border-slate-200 rounded-md bg-white"
            data-testid="user-role-select"
          >
            <option value="user">Usuario · Farmacia</option>
            <option value="admin">Administrador</option>
          </select>
          <button
            type="submit"
            data-testid="user-save-button"
            className="bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800"
          >
            Crear
          </button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="users-filter-bar">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            filter === "all" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
          data-testid="filter-all"
        >
          Todos ({users.length})
        </button>
        <button
          onClick={() => setFilter("inactive")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            filter === "inactive"
              ? "bg-rose-600 text-white"
              : inactiveCount > 0
              ? "bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100"
              : "bg-white border border-slate-200 text-slate-400"
          }`}
          data-testid="filter-inactive"
        >
          <AlertTriangle className="h-3 w-3" />
          Inactivos ≥{INACTIVITY_THRESHOLD_DAYS}d ({inactiveCount})
        </button>
        {filter === "inactive" && inactiveCount > 0 && (
          <button
            onClick={bulkSendReminders}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700"
            data-testid="bulk-reminder-button"
          >
            <Mail className="h-3 w-3" />
            Enviar recordatorio a todos
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="p-4 text-left font-semibold">Email / Licencia</th>
              <th className="p-4 text-left font-semibold">Nombre</th>
              <th className="p-4 text-left font-semibold">Plan</th>
              <th className="p-4 text-left font-semibold">
                <div className="inline-flex items-center gap-1">
                  <Activity className="h-3 w-3" /> Actividad
                </div>
              </th>
              <th className="p-4 text-left font-semibold">Creado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {visibleUsers.map((u) => {
              const inactive = isInactiveUser(u);
              const ds = daysSince(u.last_login_at);
              return (
              <tr key={u.id} className={`hover:bg-slate-50 ${inactive ? "bg-rose-50/30" : ""}`}>
                <td className="p-4">
                  <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                    {u.email}
                    {u.is_blocked && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded-full font-bold"
                        title={u.blocked_reason || "Acceso bloqueado por el admin"}
                        data-testid={`blocked-badge-${u.id}`}
                      >
                        <Lock className="h-2.5 w-2.5" /> Bloqueado
                      </span>
                    )}
                    {inactive && !u.is_blocked && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded-full font-bold border border-rose-300"
                        title={`Sin entrar ${ds == null ? "nunca" : `desde hace ${ds} días`}`}
                        data-testid={`inactive-badge-${u.id}`}
                      >
                        Inactivo
                      </span>
                    )}
                  </div>
                  {u.license_code && (
                    <div className="text-[10px] font-mono-num text-slate-400 mt-0.5">Lic. {u.license_code}</div>
                  )}
                </td>
                <td className="p-4 text-slate-600">{u.name || "—"}</td>
                <td className="p-4">
                  {u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-slate-900 text-white">
                      <Shield className="h-3 w-3" /> Admin
                    </span>
                  ) : u.license_type ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border ${LICENSE_TYPE_BADGES[u.license_type] || ""}`}>
                      <CreditCard className="h-3 w-3" />
                      {LICENSE_TYPE_LABELS[u.license_type] || u.license_type}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                      Sin plan
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <div className="text-xs text-slate-700">
                    <div className="font-mono-num">
                      <strong className="text-slate-900">{u.login_count || 0}</strong>
                      <span className="text-slate-400 ml-1">{(u.login_count || 0) === 1 ? "login" : "logins"}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5" title={u.last_login_at || ""}>
                      {u.last_login_at ? `último: ${formatRelative(u.last_login_at)}` : "nunca ha entrado"}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-xs font-mono-num text-slate-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}
                </td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    {u.role !== "admin" && userHasRealEmail(u) && (u.studies_used || 0) > 0 && (
                      <button
                        data-testid={`send-monthly-summary-${u.id}`}
                        onClick={() => sendMonthlySummary(u)}
                        disabled={!!reminderLoading[`sum-${u.id}`]}
                        className="p-1.5 text-slate-400 hover:text-emerald-600 disabled:opacity-50 disabled:cursor-wait"
                        title={`Enviar resumen mensual "Tu mes en FarmaClass" a ${u.email}`}
                      >
                        <BarChart3 className="h-4 w-4" />
                      </button>
                    )}
                    {u.role !== "admin" && userHasRealEmail(u) && (
                      <button
                        data-testid={`send-welcome-${u.id}`}
                        onClick={() => sendWelcomeAccess(u)}
                        disabled={!!reminderLoading[`wc-${u.id}`]}
                        className="p-1.5 text-slate-400 hover:text-sky-600 disabled:opacity-50 disabled:cursor-wait"
                        title={`Enviar acceso (email + URL, sin resetear contraseña) a ${u.email}`}
                      >
                        <LogIn className="h-4 w-4" />
                      </button>
                    )}
                    {u.role !== "admin" && userHasRealEmail(u) && (
                      <button
                        data-testid={`send-reminder-${u.id}`}
                        onClick={() => sendReminder(u)}
                        disabled={!!reminderLoading[u.id]}
                        className="p-1.5 text-slate-400 hover:text-blue-600 disabled:opacity-50 disabled:cursor-wait"
                        title={inactive
                          ? `Reactivar: enviar email de recordatorio${ds != null ? ` (${ds}d sin entrar)` : ""}`
                          : "Enviar email de recordatorio"}
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button
                        data-testid={`change-plan-${u.id}`}
                        onClick={() => openPlanChange(u)}
                        className="p-1.5 text-slate-400 hover:text-blue-600"
                        title="Cambiar plan"
                      >
                        <CreditCard className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      data-testid={`edit-user-${u.id}`}
                      onClick={() => openEdit(u)}
                      className="p-1.5 text-slate-400 hover:text-emerald-700"
                      title="Editar datos de acceso (email / nombre)"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`view-creds-${u.id}`}
                      onClick={() => openCredentials(u)}
                      className="p-1.5 text-slate-400 hover:text-violet-700"
                      title="Ver contraseña actual"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`reset-pass-${u.id}`}
                      onClick={() => {
                        setResetFor(u);
                        setNewPass("");
                      }}
                      className="p-1.5 text-slate-400 hover:text-slate-900"
                      title="Cambiar contraseña"
                    >
                      <KeyRound className="h-4 w-4" />
                    </button>
                    {u.role !== "admin" && u.id !== me?.id && (
                      <button
                        data-testid={`toggle-block-${u.id}`}
                        onClick={() => toggleBlock(u)}
                        className={`p-1.5 ${u.is_blocked ? "text-red-600 hover:text-emerald-700" : "text-slate-400 hover:text-red-600"}`}
                        title={u.is_blocked
                          ? "Desbloquear acceso (reactivar cuenta)"
                          : "Bloquear acceso · verá 'Su suscripción ha caducado' al entrar"}
                      >
                        {u.is_blocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                      </button>
                    )}
                    {u.id !== me?.id && (
                      <button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => remove(u)}
                        className="p-1.5 text-slate-400 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal: cambiar contraseña */}
      {resetFor && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" data-testid="reset-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight">Nueva contraseña</h3>
                <p className="text-sm text-slate-500 mt-1">{resetFor.email}</p>
              </div>
              <button onClick={() => setResetFor(null)} className="p-1 text-slate-400 hover:text-slate-900">
                <X className="h-4 w-4" />
              </button>            </div>
            <input
              type="text"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              placeholder="Contraseña…"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-md mb-4 font-mono-num"
              autoFocus
              data-testid="reset-pass-input"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setResetFor(null)}
                className="px-4 py-2 border border-slate-200 rounded-md text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={resetPassword}
                data-testid="reset-pass-confirm"
                className="bg-slate-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ver/recuperar contraseña actual del usuario */}
      {credsFor && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" data-testid="creds-modal" onClick={() => setCredsFor(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight inline-flex items-center gap-2">
                  <Eye className="h-5 w-5 text-violet-600" /> Credenciales del usuario
                </h3>
                <p className="text-sm text-slate-500 mt-1">Visible solo para administradores.</p>
              </div>
              <button onClick={() => setCredsFor(null)} className="p-1 text-slate-400 hover:text-slate-900" data-testid="close-creds-modal">
                <X className="h-5 w-5" />
              </button>
            </div>

            {credsLoading ? (
              <div className="py-8 text-center text-sm text-slate-500">Cargando…</div>
            ) : credsData ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Email de acceso</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={credsData.email || ""}
                      className="flex-1 px-3 py-2 border border-slate-200 bg-slate-50 rounded-md text-sm font-mono-num"
                      data-testid="creds-email"
                    />
                    <button
                      onClick={() => copyText(credsData.email)}
                      className="p-2 text-slate-500 hover:text-slate-900"
                      title="Copiar email"
                      data-testid="copy-creds-email"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Contraseña</label>
                  {credsData.current_password ? (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={credsData.current_password || ""}
                          className="flex-1 px-3 py-2 border border-emerald-200 bg-emerald-50 rounded-md text-sm font-mono-num font-bold tracking-wider text-emerald-900"
                          data-testid="creds-password"
                        />
                        <button
                          onClick={() => copyText(credsData.current_password)}
                          className="p-2 text-slate-500 hover:text-slate-900"
                          title="Copiar contraseña"
                          data-testid="copy-creds-password"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="text-[11px] text-amber-700 mt-1">
                        ⚠️ Cópiala ahora: no se almacena y no podrás volver a verla.
                      </div>
                    </>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 inline-flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-slate-600">
                        Por seguridad las contraseñas <strong>no se almacenan en claro</strong>.
                        Pulsa "Generar nueva contraseña" para crear una y comunicársela al cliente
                        (se mostrará una única vez).
                      </div>
                    </div>
                  )}
                  {credsData.password_changed_at && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Última modificación: {new Date(credsData.password_changed_at).toLocaleString("es-ES")}
                    </div>
                  )}
                </div>

                {credsData.license_code && (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
                    El usuario también puede entrar con su código de licencia: <span className="font-mono-num font-bold">{credsData.license_code}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-2 pt-3 border-t border-slate-200">
                  <button
                    onClick={generatePassword}
                    className="px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2"
                    data-testid="creds-regenerate"
                  >
                    <KeyRound className="h-4 w-4" /> Generar nueva contraseña
                  </button>
                  <button
                    onClick={() => setCredsFor(null)}
                    className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Modal: editar datos del usuario (email / nombre / farmacia) */}
      {editFor && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" data-testid="edit-user-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight">Editar datos de acceso</h3>
                <p className="text-sm text-slate-500 mt-1">Corrige email o nombre si se dieron de alta mal.</p>
                <p className="text-[11px] text-slate-400 mt-0.5">ID: <span className="font-mono-num">{editFor.id}</span></p>
              </div>
              <button onClick={() => setEditFor(null)} className="p-1 text-slate-400 hover:text-slate-900">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Email de acceso</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono-num"
                  data-testid="edit-email-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Nombre del titular</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  data-testid="edit-name-input"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Nombre comercial de la farmacia</label>
                <input
                  type="text"
                  value={editForm.pharmacy_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, pharmacy_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  placeholder="Ej. Farmacia García"
                  data-testid="edit-pharmacy-input"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono-num"
                  placeholder="Ej. 912 345 678"
                  data-testid="edit-phone-input"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Provincia</label>
                <select
                  value={editForm.province}
                  onChange={(e) => setEditForm((f) => ({ ...f, province: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                  data-testid="edit-province-input"
                >
                  <option value="">— Sin especificar —</option>
                  {provinces.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Comunidad autónoma:{" "}
                  <strong className="text-slate-700">
                    {editForm.province ? (provincesCCAA[editForm.province] || "—") : "—"}
                  </strong>
                  <span className="text-slate-400"> · se ajusta automáticamente al cambiar la provincia.</span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setEditFor(null)}
                className="px-4 py-2 border border-slate-200 rounded-md text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit}
                data-testid="edit-user-confirm"
                className="bg-slate-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                {savingEdit ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: cambiar plan */}
      {planFor && (
        <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" data-testid="plan-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight">Cambiar plan</h3>
                <p className="text-sm text-slate-500 mt-1">{planFor.email} · {planFor.name}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Plan actual: <strong>{LICENSE_TYPE_LABELS[planFor.license_type] || "sin plan"}</strong>
                </p>
              </div>
              <button onClick={() => setPlanFor(null)} className="p-1 text-slate-400 hover:text-slate-900">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              {["single_use", "subscription_monthly", "subscription_annual"].map((lt) => (
                <label
                  key={lt}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    planForm.license_type === lt ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`plan-option-${lt}`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={lt}
                    checked={planForm.license_type === lt}
                    onChange={(e) => setPlanForm({ ...planForm, license_type: e.target.value })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{LICENSE_TYPE_LABELS[lt]}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {lt === "single_use" && "1 estudio puntual · 120 €"}
                      {lt === "subscription_monthly" && "Acceso continuo · 30 €/mes"}
                      {lt === "subscription_annual" && "Acceso anual (ahorro 24%) · 275 €/año"}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {planForm.license_type !== "single_use" && (
              <label className="flex items-start gap-2 mb-3 text-sm text-slate-700" data-testid="plan-consent">
                <input
                  type="checkbox"
                  checked={planForm.billing_consent}
                  onChange={(e) => setPlanForm({ ...planForm, billing_consent: e.target.checked })}
                  className="mt-1"
                />
                <span>Confirmo que el cliente ha dado consentimiento de facturación para esta suscripción.</span>
              </label>
            )}

            <label className="flex items-start gap-2 mb-4 text-sm text-slate-700" data-testid="plan-reset">
              <input
                type="checkbox"
                checked={planForm.reset_studies}
                onChange={(e) => setPlanForm({ ...planForm, reset_studies: e.target.checked })}
                className="mt-1"
              />
              <span>Resetear el contador de estudios usados a 0 (recomendado si venía de un plan "Estudio").</span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPlanFor(null)}
                className="px-4 py-2 border border-slate-200 rounded-md text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={savePlan}
                data-testid="plan-save-button"
                className="bg-slate-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800"
              >
                Guardar plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
